import { apiFetch } from './client';
import type { Page } from './discover';

/** What a story can be reported for. The backend names them; we never guess. */
export interface ReportKind {
  value: string;
  label: string;
}

export interface MyReport {
  id: string;
  kind: string;
  kind_label: string;
  status: 'OPEN' | 'REVIEWING' | 'RESOLVED' | 'DISMISSED';
  status_label: string;
  post: { id: string; title: string };
  /** Which comment, when the report was about one rather than the story (Phase 18). */
  comment: { id: string; excerpt: string } | null;
  created_at: string;
}

/** A comment as the moderator deciding a complaint about it sees it: in full. */
export interface ReportedComment {
  id: string;
  body: string;
  author: string;
  is_hidden: boolean;
  hidden_reason: string;
  created_at: string;
}

/**
 * One report, as the moderator who has to decide it sees it.
 *
 * `comment` is the same field as the reporter's own view of it, carrying more:
 * they are shown an excerpt to tell their own reports apart, the moderator is
 * shown the whole thing because the decision is about these exact words.
 */
export interface Report extends Omit<MyReport, 'comment'> {
  details: string;
  note: string;
  reporter: { handle: string; exists: boolean };
  moderator: { handle: string; exists: boolean } | null;
  post: { id: string; title: string; status: string; author: string; open_reports: number };
  comment: ReportedComment | null;
  claimed_at: string | null;
  decided_at: string | null;
}

export interface ReportedStory {
  id: string;
  title: string;
  status: string;
  author: string;
  open_reports: number;
}

function send<T>(path: string, method: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

export const listReportKinds = async (): Promise<ReportKind[]> =>
  (await apiFetch<{ kinds: ReportKind[] }>('/posts/report-kinds')).kinds;

export const reportStory = (postId: string, kind: string, details: string) =>
  send<{ id: string; status: string; detail: string }>(`/posts/${postId}/report`, 'POST', {
    kind,
    details,
  });

export const listMyReports = (): Promise<Page<MyReport>> => apiFetch<Page<MyReport>>('/me/reports');

export const listReports = (kind = ''): Promise<Page<Report>> =>
  apiFetch<Page<Report>>(`/moderation/reports${kind ? `?kind=${kind}` : ''}`);

export const listReportedStories = (): Promise<Page<ReportedStory>> =>
  apiFetch<Page<ReportedStory>>('/moderation/reported-stories');

export const claimReport = (id: string): Promise<Report> =>
  send<Report>(`/moderation/reports/${id}`, 'POST');

export const decideReport = (id: string, decision: 'resolve' | 'dismiss', note: string) =>
  send<Report>(`/moderation/reports/${id}?decision=${decision}`, 'PUT', { note });
